import { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Eye, Download, X } from "lucide-react";

const CATEGORIES = ["KYC", "GST", "ITR", "TDS", "Audit", "Bank", "Agreements", "Misc"];

const MAX_BYTES = 50 * 1024 * 1024;

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function iconForType(ct, name) {
  const ext = (name || "").split(".").pop()?.toLowerCase();
  if (ct?.startsWith("image/")) return "🖼";
  if (ext === "pdf") return "📄";
  if (["xlsx", "xls", "csv"].includes(ext)) return "📊";
  if (["doc", "docx"].includes(ext)) return "📝";
  return "📁";
}

export default function ClientDocs({ clientId, canWrite, canDelete }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("KYC");

  const load = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/documents`);
      setDocs(data);
    } catch (e) {
      toast.error("Could not load documents");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 50 MB limit");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${clientId}/documents?category=${encodeURIComponent(category)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/clients/${clientId}/documents/${id}`);
      toast.success("Document deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const previewUrl = (d) => {
    const token = localStorage.getItem("taxinpho_token");
    return `${API}/clients/${clientId}/documents/${d.id}/download?auth=${encodeURIComponent(token)}`;
  };

  const download = async (d) => {
    try {
      const token = localStorage.getItem("taxinpho_token");
      const res = await fetch(`${API}/clients/${clientId}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { toast.error("Download failed"); }
  };

  if (!clientId) {
    return (
      <div className="p-6 text-center text-sm text-zinc-500 font-mono border border-dashed border-zinc-300">
        [ Save the client first to upload documents ]
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-docs-tab">
      <div className="border border-zinc-200 p-4 bg-[#FAFAFA]">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="label-caps block mb-1.5 text-[10px]">Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="doc-category"
              className="w-full border border-zinc-300 px-3 py-2 bg-white text-sm"
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 font-semibold text-sm ${busy ? "opacity-60" : ""} bg-zinc-900 text-white hover:bg-zinc-800`}>
            <Upload size={14} />
            {busy ? "Uploading..." : "Upload file"}
            <input
              type="file"
              className="hidden"
              onChange={handleFile}
              disabled={busy || !canWrite}
              data-testid="doc-upload-input"
            />
          </label>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Any file type · up to 50 MB · stored securely in Emergent Object Storage.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="p-10 text-center text-sm text-zinc-500 font-mono border border-zinc-200">
          [ no documents uploaded yet ]
        </div>
      ) : (
        <div className="border border-zinc-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-2 label-caps text-[10px]">File</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Category</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Size</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Uploaded</th>
                <th className="text-right px-4 py-2 label-caps text-[10px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{iconForType(d.content_type, d.filename)}</span>
                      <span className="font-medium truncate max-w-[260px]">{d.filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-xs bg-[#EFF4FF] text-[#002FA7] border border-[#002FA7] px-2 py-0.5">
                      {d.category || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{humanSize(d.size)}</td>
                  <td className="px-4 py-2 text-xs text-zinc-500">
                    {new Date(d.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <a href={previewUrl(d)} target="_blank" rel="noreferrer"
                       title="Preview"
                       data-testid={`doc-preview-${d.id}`}
                       className="inline-flex p-1.5 hover:bg-zinc-100">
                      <Eye size={14} />
                    </a>
                    <button onClick={() => download(d)} title="Download"
                       data-testid={`doc-download-${d.id}`}
                       className="inline-flex p-1.5 hover:bg-zinc-100">
                      <Download size={14} />
                    </button>
                    {canDelete && (
                      <button onClick={() => remove(d.id)} title="Delete"
                         data-testid={`doc-delete-${d.id}`}
                         className="inline-flex p-1.5 hover:bg-red-50 text-[#D92D20]">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
